import { createContext, useState, useContext, useCallback, ReactNode, Dispatch, SetStateAction } from "react";
import useLocalStorage from "../hooks/useLocalStorage";

/* *** */
interface UIContextType {
  hideEmptyCategory: boolean;
  toggleHideEmptyCategory: () => void;
  setHideEmptyCategory: (value: boolean) => void; // We still provide the raw setter just in case a component needs it; This type allows both direct values setHideEmptyCategory(false) and functional updates setHideEmptyCategory(prev => !prev)
}
const UIContext = createContext<UIContextType | undefined>(undefined); // "undefined" helps us catch errors if we try to use the context outside a Provider

/* *** */
interface UIProviderProps {
  children: ReactNode;
}
function UIProvider({ children }: UIProviderProps) {
  const [hideEmptyCategory, setHideEmptyCategory] = useLocalStorage("ui_hide_empty_category", true);

  // useCallback ensures this function identity stays stable across renders (won't be recreated on every render)
  const toggleHideEmptyCategory = useCallback(() => {
    setHideEmptyCategory((prev) => !prev);
  }, []);

  return (
    <UIContext.Provider value={{ hideEmptyCategory, toggleHideEmptyCategory, setHideEmptyCategory }}>
      {children}
    </UIContext.Provider>
  );
}

/* *** */
function useUI(): UIContextType {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error("useUI must be used within a <UIProvider></UIProvider>");
  }
  return context;
}
export { UIProvider, useUI };
